<div class="conditional-dialog">
  <header class="conditional-header">
    <div class="conditional-header-copy">
      <h2 agText="body-2-medium">Visibility conditions</h2>
      <p agText="body-3">Set when and to whom this section is visible. By default, all content is shown to all guests at all times.</p>
    </div>
    <flow-button-v2 type="secondary" size="small" [noBorder]="true" [icon]="iconService.Icon.cross" ariaLabel="Close" (click)="closeDialog()"></flow-button-v2>
  </header>

  <div class="conditional-rule-bar">
    <flow-segment-control [segmentPadding]="'0px 8px'" [segments]="visibilitySegments" [selectedValue]="visibilityMode" (segmentSelected)="setVisibilityMode($event)"></flow-segment-control>
    <span agText="body-3">this section when</span>
    <flow-segment-control [segmentPadding]="'0px 8px'" [segments]="matchSegments" [selectedValue]="mainSet.matchMode" (segmentSelected)="setMatchMode($event, mainSet)"></flow-segment-control>
    <span agText="body-3">conditions are met.</span>
  </div>

  <div class="conditional-body">
    @if (isLoading) {
      <div class="conditional-loader">
        <mat-spinner class="purple" [diameter]="32" color="primary"></mat-spinner>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="conditionGroupsTemplate; context: { set: mainSet }"></ng-container>

      <section class="conditional-exceptions">
        <div class="conditional-exceptions-row">
          <div class="conditional-exceptions-copy">
            <h3 agText="body-3-medium">Exceptions</h3>
            <p agText="caption-1">Add exceptions to visibility conditions set above.</p>
          </div>
          <flow-switch [checked]="exceptionsEnabled" ariaLabel="Toggle exceptions" (checkedChange)="toggleExceptions($event)"></flow-switch>
        </div>

        @if (exceptionsEnabled) {
          <div class="conditional-rule-bar conditional-rule-bar-exception">
            <span agText="body-3">Hide this section when</span>
            <flow-segment-control [segmentPadding]="'0px 8px'" [segments]="matchSegments" [selectedValue]="exceptionSet.matchMode" (segmentSelected)="setMatchMode($event, exceptionSet)"></flow-segment-control>
            <span agText="body-3">conditions are met.</span>
          </div>

          <div class="conditional-exceptions-list">
            <ng-container *ngTemplateOutlet="conditionGroupsTemplate; context: { set: exceptionSet }"></ng-container>
          </div>
        }
      </section>
    }
  </div>

  <footer class="conditional-footer">
    @for (warning of conditionWarnings(); track warning) {
      <div class="conditional-warning">
        <img class="conditional-summary-icon" [src]="iconService.Icon.i_letter_1_5" alt="" aria-hidden="true" />
        <p class="conditional-warning-text" agText="body-3">{{ warning }}</p>
      </div>
    }
    @if (hasConditionSummary) {
      <div class="conditional-summary">
        <img class="conditional-summary-icon" [src]="iconService.Icon.i_letter_1_5" alt="" aria-hidden="true" />
        <p class="conditional-summary-text" agText="body-3">
          @if (mainSummaryText) {
            <strong>{{ summaryLeadIn }}</strong>
            @for (part of mainSummaryParts; track $index) {
              @if (!$first) {
                <strong class="conditional-summary-joiner">{{ setJoiner(mainSet) }}</strong>
              }
              {{ part }}
            }
          }
          @if (exceptionSummaryText) {
            <strong>{{ exceptionLeadIn }}</strong>
            @for (part of exceptionSummaryParts; track $index) {
              @if (!$first) {
                <strong class="conditional-summary-joiner">{{ setJoiner(exceptionSet) }}</strong>
              }
              {{ part }}
            }
          }
        </p>
      </div>
    }

    <div class="conditional-footer-actions">
      <flow-button-v2 label="Cancel" type="secondary" (click)="closeDialog()"></flow-button-v2>
      <flow-button-v2 label="Save" type="primary" [disabled]="disableConfirmButton()" (click)="saveFunction()"></flow-button-v2>
    </div>
  </footer>
</div>

<ng-template #conditionGroupsTemplate let-set="set">
  @for (group of conditionGroups; track group.title) {
    @if (group.conditions.length) {
      <section class="condition-group">
        <button type="button" class="condition-group-header" [attr.aria-expanded]="!isGroupCollapsed(group.title, set)" (click)="toggleGroup(group.title, set)">
          <span class="condition-group-title" agText="caption-1-medium">{{ group.title }}</span>
          <span class="condition-group-summary" agText="body-3">{{ groupSummary(group, set) }}</span>
          <img class="condition-group-chevron" [class.condition-group-chevron-expanded]="!isGroupCollapsed(group.title, set)" [src]="iconService.Icon.down_1" alt="" aria-hidden="true" />
        </button>
        @if (!isGroupCollapsed(group.title, set)) {
          <div class="condition-group-list">
            @for (condition of group.conditions; track condition.value + (condition.fieldName ?? "")) {
              <flow-card-v2
                [title]="conditionLabel(condition)"
                titleSize="small"
                [leadingIcon]="conditionIcon(condition)"
                leadingIconVariant="boxed"
                [disabledText]="conditionDisabledText(condition, set)"
                [trailingBadge]="conditionBadge(condition, set)"
                [collapsible]="true"
                [expanded]="false"
                [bodyPadding]="'0px'"
                (expandedChange)="onConditionExpanded(condition, $event, set)"
                [chevronAriaLabel]="'Toggle ' + conditionLabel(condition)">
                @let index = filterIndexFor(condition, set);
                @if (index !== -1) {
                  @let filter = set.filterList[index];
                  @if (condition.value === "Reservation:Status") {
                    <p class="fg-subtle guidance-desc" agText="body-3">Select the stay phase(s) when this section should {{ visibilityModeLabel }}.</p>
                    @if (hasLegacyStatusComparer(index, set)) {
                      <div class="legacy-rule-warning" role="alert">
                        <img [src]="iconService.Icon.exclamation_mark_1_5" alt="" aria-hidden="true" width="16" height="16" />
                        <div class="legacy-rule-warning-copy">
                          <h5 agText="body-3-medium">Condition isn't available anymore</h5>
                          <p agText="body-3">
                            This was set to <strong>{{ legacyStatusSummary(index, set) }}</strong>. {{ legacyStatusFixHint(index, set) }}
                          </p>
                        </div>
                      </div>
                    }
                    <div class="flex-column" style="padding: 16px 20px 20px 20px">
                      <div class="condition-row">
                        <flow-select
                          [width]="480"
                          class="condition-field"
                          placeholder="Select status"
                          [showSelectionCount]="true"
                          [items]="reservationStatusItems(filter)"
                          [dividers]="reservationStatusDividers"
                          [footer]="reservationStatusFooter"
                          (selectionChange)="onSetReservationStatus($event, index, set)"></flow-select>
                      </div>
                      @if (filter.reservationStatuses?.length) {
                        <div class="status-chip-row">
                          @for (status of filter.reservationStatuses; track status.value) {
                            <flow-chip color="Info1" type="Subtle" [size]="'Large'" [text]="status.label" (close)="onRemoveReservationStatus(status, index, set)"></flow-chip>
                          }
                        </div>
                      }
                    </div>
                  }

                  @if (condition.value === "StartDateTime" || condition.value === "EndDateTime") {
                    @let isStart = condition.value === "StartDateTime";
                    <div class="date-time-condition">
                      <p class="date-time-description" agText="body-3">Set {{ isStart ? "a start" : "an end" }} date &amp; time.</p>
                      <div class="date-time-fields">
                        <label class="date-time-label" agText="body-3-medium">{{ visibilityModeLabel }} {{ isStart ? "from" : "until" }}</label>
                        <div class="date-time-row">
                          <ag-date-picker
                            class="date-time-field"
                            disableDates="past"
                            placement="bottom"
                            placeholder="Set date"
                            forceWidth="100%"
                            [startDate]="toDate(isStart ? filter.dateStart : filter.dateEnd)"
                            (dateRangeOutput)="onSetDate($event, index, isStart ? 'dateStart' : 'dateEnd', set)"></ag-date-picker>
                          <div class="date-time-field time-span-input">
                            <input
                              type="text"
                              class="time-of-day-input"
                              placeholder="Set time"
                              pattern="^([01]\d|2[0-3]):([0-5]\d)$"
                              [value]="(isStart ? filter.timeSet?.from : filter.timeSet?.to) ?? ''"
                              (keydown)="onTimeKeydown($event)"
                              (change)="onTimeInputChange($event, isStart ? 'from' : 'to', index, null, set)" />
                            <img [src]="iconService.Icon.clock" alt="" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  @if (condition.value === "WeekTimeSpan") {
                    <p class="fg-subtle day-of-week-description" agText="body-3">{{ visibilityModeLabel }} section only on selected days & hours.</p>
                    <div class="condition-stack week-time-stack">
                      <div class="weekday-section">
                        <div class="condition-copy">
                          <h5 agText="body-3">Select days</h5>
                        </div>
                        <div class="weekday-row">
                          @for (day of orderedWeekDays; track day) {
                            <flow-pill [width]="60" [label]="day" [icon]="''" [active]="filter.selectedWeekDays?.includes(day)" (pillClick)="onSelectDay(day, index, set)"></flow-pill>
                          }
                        </div>
                      </div>

                      <hr class="condition-divider" />
                      <div class="condition-column time-config-column">
                        <h5 agText="body-3-medium">Time configuration</h5>
                        <flow-select [foldUp]="true" [width]="310" [items]="timeConfigItems(filter)" (selectionChange)="onTimeConfigChange($event, index, set)"></flow-select>
                      </div>
                      @if (filter.visibilityDays?.timeConfig?.value === "SameHoursSelectedDays" && filter.selectedWeekDays?.length > 0) {
                        <div class="time-of-day-band">
                          <div class="time-of-day-container">
                            <h5 class="time-of-day-label" agText="body-3-medium">All selected days</h5>
                            <div class="time-of-day-fields">
                              <div class="check-box-section">
                                <flow-checkbox-v2 size="small" [checked]="filter.visibilityDays?.AllDay" (checkedChange)="onAllDayChange($event, index, null, set)"></flow-checkbox-v2>
                                <span agText="body-3">{{ visibilityModeLabel }} all day (24 hours).</span>
                              </div>
                              @if (!filter.visibilityDays?.AllDay) {
                                <div class="condition-row condition-row-center">
                                  <div class="time-span-input">
                                    <input type="text" class="time-of-day-input" placeholder="HH:MM" pattern="^([01]\d|2[0-3]):([0-5]\d)$" (keydown)="onTimeKeydown($event)" [value]="filter.timeSet?.from ?? ''" (change)="onTimeInputChange($event, 'from', index, null, set)" />
                                    <img [src]="iconService.Icon.clock" alt="" aria-hidden="true" />
                                  </div>
                                  <span agText="body-3">to</span>
                                  <div class="time-span-input">
                                    <input type="text" class="time-of-day-input" placeholder="HH:MM" pattern="^([01]\d|2[0-3]):([0-5]\d)$" (keydown)="onTimeKeydown($event)" [value]="filter.timeSet?.to ?? ''" (change)="onTimeInputChange($event, 'to', index, null, set)" />
                                    <img [src]="iconService.Icon.clock" alt="" aria-hidden="true" />
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                        </div>
                      } @else if (filter.visibilityDays?.timeConfig?.value === "DifferentHoursSelectedDays" && filter.selectedWeekDays?.length > 0) {
                        <div class="time-of-day-band">
                          @for (day of weekDays; track day) {
                            @if (filter.selectedWeekDays?.includes(day)) {
                              <div class="time-of-day-container">
                                <h5 class="time-of-day-label" agText="body-3-medium">{{ weekDayLabels[day] ?? day }}</h5>
                                <div class="time-of-day-fields">
                                  <div class="check-box-section">
                                    <flow-checkbox-v2 size="small" [checked]="filter.visibilityDays[dayNameFromLabel(day)]?.AllDay" (checkedChange)="onAllDayChange($event, index, day, set)"></flow-checkbox-v2>
                                    <span agText="body-3">Available all day (24 hours).</span>
                                  </div>
                                  @if (!filter.visibilityDays[dayNameFromLabel(day)]?.AllDay) {
                                    <div class="condition-row condition-row-center">
                                      <div class="time-span-input">
                                        <input
                                          type="text"
                                          class="time-of-day-input"
                                          placeholder="HH:MM"
                                          pattern="^([01]\d|2[0-3]):([0-5]\d)$"
                                          (keydown)="onTimeKeydown($event)"
                                          [value]="filter.visibilityDays[dayNameFromLabel(day)]?.from ?? ''"
                                          (change)="onTimeInputChange($event, 'from', index, day, set)" />
                                        <img [src]="iconService.Icon.clock" alt="" aria-hidden="true" />
                                      </div>
                                      <span agText="body-3">to</span>
                                      <div class="time-span-input">
                                        <input type="text" class="time-of-day-input" placeholder="HH:MM" pattern="^([01]\d|2[0-3]):([0-5]\d)$" (keydown)="onTimeKeydown($event)" [value]="filter.visibilityDays[dayNameFromLabel(day)]?.to ?? ''" (change)="onTimeInputChange($event, 'to', index, day, set)" />
                                        <img [src]="iconService.Icon.clock" alt="" aria-hidden="true" />
                                      </div>
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          }
                        </div>
                      }
                    </div>
                  }

                  @if (condition.dynamicVariable && condition.value !== "Reservation:Status") {
                    @let isTextFilter = filter.infofilter.value === "IList<String>" || filter.infofilter.value === "String";
                    <p class="fg-subtle guidance-desc" agText="body-3">
                      @if (isTextFilter) {
                        Only {{ visibilityModeLabel | lowercase }} for reservations containing certain words or phrases in the {{ conditionLabel(condition) | lowercase }} field in PMS.
                      } @else {
                        Only {{ visibilityModeLabel }} for reservations with this {{ conditionLabel(condition) }}(s)
                      }
                    </p>

                    @if (isTextFilter && hasLegacyMatchRule(index, set)) {
                      <div class="legacy-rule-warning" role="alert">
                        <img [src]="iconService.Icon.exclamation_mark_1_5" alt="" aria-hidden="true" width="16" height="16" />
                        <div class="legacy-rule-warning-copy">
                          <h5 agText="body-3-medium">Condition isn't available anymore</h5>
                          <p agText="body-3">
                            This was set to <strong>{{ legacyRuleSummary(index, set) }}</strong>. {{ legacyFixHint(index, set, condition) }}
                          </p>
                        </div>
                      </div>
                    }

                    @if (isTextFilter && (matchTypeSegments(index, set).length > 1 || hasLegacyMatchRule(index, set))) {
                      <div class="match-type-row">
                        <div class="match-type-copy">
                          <h5 agText="body-3-medium">Match type</h5>
                          <p agText="body-3">{{ matchTypeDescription(index, set, condition) }}</p>
                        </div>
                        <flow-segment-control [segmentPadding]="'0px 12px'" [segments]="matchTypeSegments(index, set)" [selectedValue]="activeMatchType(index, set)" (segmentSelected)="onMatchTypeChange($event, index, set)"></flow-segment-control>
                      </div>
                    }

                    <div class="condition-stack" style="padding: 20px">
                      @if (filter.infofilter.value === "IList<String>") {
                        <ng-container *ngTemplateOutlet="phraseInputTemplate; context: { condition: condition, filter: filter, index: index, set: set }"></ng-container>
                      }
                      @if (filter.infofilter.value === "Int32" || filter.infofilter.value === "Double" || filter.infofilter.value === "Decimal") {
                        <div class="range-row">
                          <div class="range-field">
                            <span class="range-label" agText="body-3-medium">Min.</span>
                            <ag-input-field
                              inputType="number"
                              [allowNegativeNumbers]="false"
                              [showDecimalValues]="filter.infofilter.value !== 'Int32'"
                              [initValue]="filter.svValues[0] ?? ''"
                              (inputResult)="onMinInput($event, index, set)"
                              placeholder="Set min"
                              [styles]="{ specificWidth: '100%' }"></ag-input-field>
                          </div>
                          <span class="range-separator" agText="body-3">to</span>
                          <div class="range-field">
                            <span class="range-label" agText="body-3-medium">Max.</span>
                            <ag-input-field
                              inputType="number"
                              [allowNegativeNumbers]="false"
                              [showDecimalValues]="filter.infofilter.value !== 'Int32'"
                              [initValue]="filter.svValues[1] ?? ''"
                              (inputResult)="onMaxInput($event, index, set)"
                              placeholder="Set max"
                              [info]="{ showInfo: true, type: 'info', text: 'Leave empty for no upper limit.' }"
                              [styles]="{ specificWidth: '100%' }"></ag-input-field>
                          </div>
                        </div>
                      }
                      @if (filter.infofilter.value === "String" && !condition.dropdownSupport) {
                        <ng-container *ngTemplateOutlet="phraseInputTemplate; context: { condition: condition, filter: filter, index: index, set: set }"></ng-container>
                      }
                      @if (filter.infofilter.value === "String" && condition.dropdownSupport) {
                        <flow-select
                          [width]="480"
                          class="condition-field"
                          [placeholder]="'Select ' + conditionLabel(condition).toLowerCase()"
                          [showSelectionCount]="true"
                          [searchbarLabel]="'Search'"
                          [items]="dropdownValueItems(filter)"
                          [footer]="dropdownValueFooter"
                          [addable]="true"
                          [addPlaceholder]="'Type in ' + conditionLabel(condition).toLowerCase()"
                          [addTriggerLabel]="'Add ' + conditionLabel(condition).toLowerCase()"
                          addedCheckbox="checkbox"
                          (selectionChange)="onSetDropdownValues($event, index, set)"
                          (itemAdded)="onDropdownValueAdded($event, filter, index, set)"
                          (itemRemoved)="onDropdownValueRemoved($event, filter, index, set)"></flow-select>
                        @if (filter.svValues?.length) {
                          <div class="status-chip-row">
                            @for (tag of filter.svValues; track tag) {
                              <flow-chip color="Info1" type="Subtle" [size]="'Large'" [text]="tag" (close)="onRemoveTag(tag, index, set)"></flow-chip>
                            }
                          </div>
                        }
                      }
                    </div>
                  }

                  @if (isConditionSet(condition, set)) {
                    <hr class="condition-divider" />
                    <div class="condition-clear">
                      <flow-button-v2 label="Clear" type="secondary" size="small" (click)="onClearCondition(condition, set)"></flow-button-v2>
                    </div>
                  }
                }
              </flow-card-v2>
            }
          </div>
        }
      </section>
    }
  }
</ng-template>

<ng-template #phraseInputTemplate let-condition="condition" let-filter="filter" let-index="index" let-set="set">
  <div class="phrase-field">
    <label class="phrase-label" agText="body-3-medium">{{ conditionLabel(condition) }}</label>
    <div class="phrase-input-row">
      <ag-input-field
        class="phrase-input"
        [initValue]="tagDraft(filter)"
        placeholder="Type in words or phrases"
        (inputResult)="onTagDraftInput($event, filter)"
        (enterPressed)="onAddTag(filter, index, set)"
        [info]="{ showInfo: true, type: 'info', text: 'Case-sensitive.' }"
        [styles]="{ specificWidth: '100%' }"></ag-input-field>
      <flow-button-v2 label="Add" type="secondary" [disabled]="!tagDraft(filter).trim()" (click)="onAddTag(filter, index, set)"></flow-button-v2>
    </div>
  </div>

  @if (filter.svValues?.length) {
    <div class="status-chip-row">
      @for (tag of filter.svValues; track tag) {
        <flow-chip color="Info1" type="Subtle" [size]="'Large'" [text]="tag" (close)="onRemoveTag(tag, index, set)"></flow-chip>
      }
    </div>
  }
</ng-template>
